import {LevelDocSchemaV2, type LevelDocV2} from "@repo/shared";

import {BaseCollectionConfig} from "../BaseCollectionConfig";
import {collectionNameV2} from "../collectionName";
import {createQueryFactory} from "../factories/createQueryFactory";
import {creationHookFactory} from "../factories/creationHookFactory";
import {getByIdHookFactory} from "../factories/getByIdHookFactory";
import {getByIdRealtimeSubFactory} from "../factories/getByIdRealtimeSubFactory";
import {lazyCallbackFactory} from "../factories/lazyCallbackFactory";
import {queryHookFactory} from "../factories/queryHookFactory";
import {queryRealtimeHookFactory} from "../factories/queryRealtimeHookFactory";
import type {Filter, Sort} from "../queryUtils";

export class LevelCollectionConfigV2 extends BaseCollectionConfig<
  typeof LevelDocSchemaV2,
  unknown
> {
  constructor() {
    super(LevelDocSchemaV2);
  }

  getCollection(): [string, ...(string | undefined)[]] {
    return [collectionNameV2("levels")];
  }

  parser(id: string, data: any): LevelDocV2 {
    return {
      id,
      ...data,
    };
  }
}

export const levelSortingOptions: {
  label: string;
  value: Sort<LevelDocV2>[];
}[] = [
  {label: "None", value: []},
  {
    label: "Recent",
    value: [
      {
        fieldPath: "publishedAt",
        direction: "desc",
      },
    ],
  },
  {
    label: "Old",
    value: [
      {
        fieldPath: "publishedAt",
        direction: "asc",
      },
    ],
  },
  {
    label: "Popular",
    value: [
      {
        fieldPath: "totalViews",
        direction: "desc",
      },
    ],
  },
  {
    label: "Latest World Records",
    value: [
      {
        fieldPath: "lastWorldRecordAt",
        direction: "desc",
      },
    ],
  },
  {
    label: "Oldest World Records",
    value: [
      {
        fieldPath: "lastWorldRecordAt",
        direction: "asc",
      },
    ],
  },
];

export const levelFilteringOptions: {
  label: string;
  value: Filter<LevelDocV2>[];
}[] = [
  {label: "Any", value: []},
  {
    label: "Easy",
    value: [
      {
        fieldPath: "successRate",
        opStr: "<",
        value: 4,
      },
    ],
  },
  {
    label: "Medium",
    value: [
      {
        fieldPath: "successRate",
        opStr: ">=",
        value: 4,
      },
      {
        fieldPath: "successRate",
        opStr: "<",
        value: 8,
      },
    ],
  },
  {
    label: "Hard",
    value: [
      {
        fieldPath: "successRate",
        opStr: ">=",
        value: 8,
      },
      {
        fieldPath: "successRate",
        opStr: "<",
        value: 16,
      },
    ],
  },
  {
    label: "Very Hard",
    value: [
      {
        fieldPath: "successRate",
        opStr: ">=",
        value: 16,
      },
      {
        fieldPath: "successRate",
        opStr: "<",
        value: 32,
      },
    ],
  },
  {
    label: "Extreme",
    value: [
      {
        fieldPath: "successRate",
        opStr: ">=",
        value: 32,
      },
    ],
  },
];

const config = new LevelCollectionConfigV2();
export const useLevelV2 = getByIdHookFactory(config);
export const useCreateLevelV2 = creationHookFactory(config);
export const useLazyCallbackLevelV2 = lazyCallbackFactory(config);
export const useLevelRealtimeSubV2 = getByIdRealtimeSubFactory(config);
export const useLevelsV2 = queryHookFactory(config);
export const useRealtimeLevelsV2 = queryRealtimeHookFactory(config);
export const runLevelsQueryV2 = createQueryFactory(config);
